/**
 * Module-scoped, single-slot stash for the wizard Data Collection state
 * across the "Add credentials" round-trip to Account Settings.
 *
 * Navigating to `/settings` unmounts the create-task wizard, and with it the
 * parent-held `stateCacheRef` snapshot — including locally attached files
 * that are only uploaded on Next. Stashing the snapshot here before the
 * navigation lets the Settings back button return the user to the exact same
 * step state (useDataCollectionHydration consumes the stash on remount
 * before falling back to server hydration).
 *
 * Keyed by usecase id and consumed at most once, so the stash cannot leak
 * into another task or shadow server hydration on a later fresh open.
 */

let stash = null;

/**
 * @param {string} key - Usecase id the snapshot belongs to.
 * @param {Object} snapshot - `stateCacheRef.current`-shaped step snapshot.
 */
export function stashDataCollectionState(key, snapshot) {
    stash = key && snapshot ? { key, snapshot } : null;
}

/**
 * Return and clear the stashed snapshot when it matches `key`; `null`
 * otherwise (a mismatched key also clears the slot — the stash is only
 * meaningful for the round-trip it was written for).
 *
 * @param {string} key - Usecase id of the mounting Data Collection step.
 * @returns {Object|null}
 */
export function consumeDataCollectionStash(key) {
    if (!stash) return null;
    const { key: stashedKey, snapshot } = stash;
    stash = null;
    return key && stashedKey === key ? snapshot : null;
}
